import { logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth, getAuthHeaders } from './client';

/**
 * @deprecated Use sendStreamingChatQueryV2 instead. This function now redirects to V2 API.
 * Chatbot: Ask a question (non-streaming)
 * RQ: { message: string, session_id: string, case_id?: string }
 * RS: { response: string, session_id: string, citations: [] }
 */
export async function sendChatQuery(payload) {
    console.warn('[DEPRECATED] sendChatQuery is deprecated. Use sendStreamingChatQueryV2 instead.');

    // Redirect to V2 API with non-streaming fallback
    return new Promise((resolve, reject) => {
        let fullResponse = '';

        sendStreamingChatQueryV2(payload, {
            onChunk: (content) => { fullResponse += content; },
            onComplete: () => {
                resolve({
                    status: 200,
                    data: { response: fullResponse, session_id: payload.session_id }
                });
            },
            onError: (error) => {
                const err = new Error(error);
                err.status = 500;
                reject(err);
            }
        });
    });
}

/**
 * @deprecated Use sendStreamingChatQueryV2 instead. This function now redirects to V2 API.
 * Chatbot: Ask a question with streaming response
 * Callbacks: onChunk(text), onComplete(), onError(error)
 */
export async function sendStreamingChatQuery(
    payload,
    onChunk,
    onComplete,
    onError
) {
    console.warn('[DEPRECATED] sendStreamingChatQuery is deprecated. Use sendStreamingChatQueryV2 instead.');

    // Redirect to V2 API
    return sendStreamingChatQueryV2(payload, {
        onChunk,
        onComplete,
        onError
    });
}

/**
 * LangGraph Chat V2: Ask a question with streaming response (RECOMMENDED)
 * Uses the new LangGraph-powered agent with multi-step reasoning
 *
 * RQ: { message: string, session_id: string, case_id?: string }
 *
 * Event Types:
 * - thinking_step: Progress indicators (step_id, title, status, items)
 * - chunk: Response text chunks (content)
 * - tool_call: Tool being invoked (name, args)
 * - tool_result: Tool results (name, result)
 * - done: Completion (session_id)
 * - error: Errors (error)
 *
 * Callbacks:
 * - onChunk(text): Called for each text chunk
 * - onThinkingStep(step): Called for progress indicators
 * - onToolCall(name, args): Called when a tool is invoked
 * - onToolResult(name, result): Called with tool results
 * - onComplete(sessionId): Called when done
 * - onError(error): Called on errors
 */
export async function sendStreamingChatQueryV2(
    payload,
    {
        onChunk,
        onThinkingStep,
        onToolCall,
        onToolResult,
        onComplete,
        onError
    }
) {
    try {
        const headers = getAuthHeaders({ "Content-Type": "application/json" });
        const url = `${BASE_URL}/api/chat/v2/ask/stream`;

        // Debug logging
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Chat V2 API] Request URL:', url);
            console.log('[Chat V2 API] Request payload:', JSON.stringify(payload, null, 2));
        }

        const response = await fetch(url, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers,
            body: JSON.stringify(payload)
        });

        if (process.env.NODE_ENV !== 'production') {
            console.log('[Chat V2 API] Response status:', response.status);
        }

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            logError("Chat V2 streaming failed:", response.status, errorText);

            if (response.status === 403) {
                onError?.("You don't have access to this case.");
            } else if (response.status === 503) {
                onError?.("AI service temporarily unavailable. Please try again.");
            } else {
                onError?.(`Unable to get response (${response.status}). Please try again.`);
            }
            return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
            onError?.("No response body");
            return;
        }

        try {
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                // Decode the chunk and add to buffer
                buffer += decoder.decode(value, { stream: true });

                // Split by newlines to get individual SSE messages
                const lines = buffer.split('\n');

                // Keep the last incomplete line in the buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (!jsonStr) continue;

                            const event = JSON.parse(jsonStr);

                            if (process.env.NODE_ENV !== 'production') {
                                console.log('[Chat V2] Event:', event.type, event);
                            }

                            switch (event.type) {
                                case 'thinking_step':
                                    onThinkingStep?.({
                                        stepId: event.step_id,
                                        title: event.title,
                                        status: event.status,
                                        items: event.items || []
                                    });
                                    break;

                                case 'chunk':
                                    onChunk?.(event.content);
                                    break;

                                case 'tool_call':
                                    onToolCall?.(event.name, event.args);
                                    break;

                                case 'tool_result':
                                    onToolResult?.(event.name, event.result);
                                    break;

                                case 'done':
                                    onComplete?.(event.session_id);
                                    return;

                                case 'error':
                                    onError?.(event.error || "An error occurred");
                                    return;

                                default:
                                    if (process.env.NODE_ENV !== 'production') {
                                        console.warn('[Chat V2] Unknown event type:', event.type);
                                    }
                            }
                        } catch (e) {
                            if (process.env.NODE_ENV !== 'production') {
                                console.warn('[Chat V2] Failed to parse event:', line, e);
                            }
                        }
                    }
                }
            }

            // If we exit the loop without seeing 'done', call onComplete
            onComplete?.();
        } finally {
            reader.releaseLock();
        }
    } catch (error) {
        logError("Chat V2 streaming error:", error);
        onError?.(error.message || "Connection error. Please try again.");
    }
}

/**
 * Check Chat V2 API status
 * GET /api/chat/v2/status
 */
export async function checkChatV2Status() {
    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/v2/status`, {
            method: "GET",
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            return { available: false, status: response.status };
        }

        const data = await response.json();
        return { available: true, ...data };
    } catch (error) {
        logError("Chat V2 status check failed:", error);
        return { available: false, error: error.message };
    }
}

/**
 * Delete a conversation
 * DELETE /api/chat/conversations/{conversation_id}
 * RS: { status: 200, data: { deleted: true } }
 */
export async function deleteConversation(conversationId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete conversation failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Conversation not found or you are not a participant.");
        }

        const error = new Error("Failed to delete conversation. Please try again.");
        error.status = response.status;
        throw error;
    }

    const data = await response.json();
    return { status: response.status, data };
}
